
import {Auction, Subject, User} from "domain-models"
import _ from "lodash"
import React from "react"
import {notify} from "react-notify-toast"
import {
  fetchIndicators,
  hideAuction,
  markAuctionCancelled,
  setAuctionFilter,
  setAuctions,
  showAuction,
  showNewlyArrived,
  updateNotifications
} from "redux/actions"
import {history} from 'Router/Router'
import {
  StartedAuctionsEvent,
  RemoveAuctionEvent,
  WonAuctionEvent,
  SpeedAuktionStartedEvent,
  SpeedAuktionClaimedEvent,
  SpeedAuktionExpiredEvent
} from "services/websockets/channels/AuctionEvents"
import {OverBidEvent} from "services/websockets/channels/BidEvents"
import {store} from 'store'
import {currency, make, model} from "../../../filters"

export const privateChannels = (user: User) => {
  window.Echo.private('user.' + user.ars_id)
    .listen('OverBid', async (event: OverBidEvent) => {
      const {auction, bid} = event
      const auctions = store.getState().auctions

      let currentAuction = _.find(auctions.auctions, {Id: auction.Id})

      if (currentAuction) {
        // Create new array with updated auction to trigger React re-render
        const updatedAuctions = auctions.auctions.map(a => {
          if (a.Id === auction.Id) {
            return {...a, isOutbid: true, round: {...a.round, agents: []}}
          }
          return a
        })
        store.dispatch(setAuctions(updatedAuctions))
      }

      // Also update the details modal if it's showing this auction
      // Create a new object to trigger React re-render (don't mutate)
      if (auctions.auction && auctions.auction.Id === auction.Id) {
        const updatedAuction = {...auctions.auction, isOutbid: true}
        store.dispatch(showAuction(updatedAuction))
      }

      const openAuction = async () => {
        await store.dispatch(hideAuction())
        
        // Refetch full auction data since websocket only sends minimal data to avoid payload size issues
        const fullAuction = await window.axios.get(`/auctions/${auction.Id}`)
        if (fullAuction && fullAuction.data) {
          await store.dispatch(showAuction(fullAuction.data))
        }

        notify.hide()
      }

      // For Type 0 (sealed bid), don't show the price
      // Note: Backend sends null bid amount for Type 0 to prevent inspection of websocket messages
      const isSealed = auction.round?.Type === 0
      const notificationText = isSealed || !bid.Bid
        ? 'Sie wurden bei einer Auktion überboten.'
        : `Sie wurden bei einer Auktion mit ${currency(bid.Bid)} überboten.`

      notify.show(
        (
          <div onClick={openAuction}>
            <span>{notificationText}</span>
          </div>
        ) as any,
        'warning',
        6000
      )

      await updateNotifications()
      await fetchIndicators()
    })
    .listen('AgentBidEvent', async () => {
      await updateNotifications()
    })
    .listen('StartedAuctions', async (event: StartedAuctionsEvent) => {
      const auctions = await window.axios.post('/getAuctions', {auctionIds: event.auctionIds})

      if (!auctions || !auctions.data || !auctions.data.data) {
        return
      }

      const openAuctions = async (auctions: Array<Auction>) => {
        if (history.location.pathname !== '/') {
          history.replace('/')
        }

        await store.dispatch(showNewlyArrived(auctions))

        notify.hide()
      }

      notify.show(
        (
          <div onClick={() => openAuctions(auctions.data.data)}>
            {event.auctionIds.length > 1 ?
              <span>Es wurden {event.auctionIds.length} neue Auktionen gestartet.</span>
              :
              <span>Es wurde 1 neue Auktion gestartet. </span>
            }
          </div> as any
        ),
        'warning',
        6000
      )
      
      // Dispatch event to refresh the main auctions list
      window.dispatchEvent(new CustomEvent('auctions-refresh'))
    })
    .listen('RemoveAuction', async (event: RemoveAuctionEvent) => {
      const auctions = store.getState().auctions
      const currentlyShownAuction = auctions.auction
      
      // Check if the cancelled auction is currently shown in the modal
      if (currentlyShownAuction && currentlyShownAuction.Id === event.auctionId) {
        // Mark as cancelled in Redux - modal will stay open showing "cancelled" status
        await store.dispatch(markAuctionCancelled(event.auctionId))
        
        notify.show(
          <span>Diese Auktion wurde abgebrochen.</span> as any,
          'error',
          8000
        )
      } else {
        // Remove from list if not currently shown
        const updatedAuctions = auctions.auctions.filter((a: Auction) => a.Id !== event.auctionId)
        
        if (updatedAuctions.length !== auctions.auctions.length) {
          await store.dispatch(setAuctions(updatedAuctions))
          
          notify.show(
            <span>Eine Auktion wurde abgebrochen.</span> as any,
            'warning',
            5000
          )
        }
      }
    })
    .listen('WonAuction', async (event: WonAuctionEvent) => {
      const openAuction = async () => {
        if (history.location.pathname !== '/') {
          await history.replace('/')
        }
        const auctions = store.getState().auctions
        const app = store.getState().app
        await setAuctionFilter(auctions.filter, {type: 'auctioned'}, app.subjectType)
        notify.hide()
      }

      notify.show(
        (
          <div onClick={openAuction}>
            <span>Sie haben eine Auktion gewonnen.</span>
          </div> as any
        ),
        'warning',
        6000
      )

      await updateNotifications()
      await fetchIndicators()
    })
    .listen('SpeedAuktionStarted', async (event: SpeedAuktionStartedEvent) => {
      // Navigate to Speed-Auktion page if not already there
      const openSpeedAuktion = () => {
        history.push('/speed-auktion')
        notify.hide()
      }

      notify.show(
        (
          <div onClick={openSpeedAuktion}>
            <span>Neue Speed-Auktion verfügbar!</span>
          </div> as any
        ),
        'warning',
        8000
      )

      // Dispatch event for SpeedAuktionPage to refresh its list
      window.dispatchEvent(new CustomEvent('speed-auktion-refresh'))
    })
    .listen('SpeedAuktionClaimed', async (event: SpeedAuktionClaimedEvent) => {
      // Notify that a Speed-Auktion was claimed (by someone else or self)
      const user = store.getState().app.user
      
      if (event.claimedBy === user?.ars_id) {
        notify.show(
          <span>Sie haben die Speed-Auktion erfolgreich beansprucht!</span> as any,
          'success',
          5000
        )
      } else {
        notify.show(
          <span>Eine Speed-Auktion wurde von einem anderen Partner beansprucht.</span> as any,
          'warning',
          5000
        )
      }

      // Dispatch event for SpeedAuktionPage to refresh its list
      window.dispatchEvent(new CustomEvent('speed-auktion-refresh'))
    })
    .listen('SpeedAuktionExpired', async (event: SpeedAuktionExpiredEvent) => {
      const auctions = store.getState().auctions
      const currentlyShownAuction = auctions.auction
      
      // Check if the expired Speed-Auktion is currently shown in the modal
      if (currentlyShownAuction && currentlyShownAuction.Id === event.auctionId) {
        // Mark as cancelled in Redux - modal will stay open showing "cancelled" status
        await store.dispatch(markAuctionCancelled(event.auctionId))
        
        notify.show(
          <span>Diese Speed-Auktion ist abgelaufen.</span> as any,
          'error',
          8000
        )
      } else {
        // Remove from list if not currently shown
        const updatedAuctions = auctions.auctions.filter((a: Auction) => a.Id !== event.auctionId)
        
        if (updatedAuctions.length !== auctions.auctions.length) {
          await store.dispatch(setAuctions(updatedAuctions))
          
          notify.show(
            <span>Eine Speed-Auktion ist abgelaufen.</span> as any,
            'warning',
            5000
          )
        }
      }

      // Dispatch event for SpeedAuktionPage to refresh its list
      window.dispatchEvent(new CustomEvent('speed-auktion-refresh'))
    })
}
